import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContentful } from '../contentful-context';
import { getImageUrlByName } from '../utils';
import RedwoodAssembly from '../../assets/redwood-assembly.mov';
import ConstruxFrameImage from '../../assets/construx-frame.jpg';
import { useScrollDepthTracking, useCTATracking } from '../../utils/tracking';
import { 
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal
} from '@floating-ui/react';
import { 
  FaCogs, 
  FaTruck, 
  FaHome, 
  FaCheckCircle, 
  FaRulerCombined,
  FaClock,
  FaLeaf,
  FaShieldAlt,
  FaTools,
  FaHandshake,
  FaPlay,
  FaTimes,
  FaExpand,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

// Assembly Video Section - at very top of page
function AssemblyVideoSection({ trackCTAClick, isExperiment }) {
  return (
    <>
      {/* Video Section */}
      <section className="w-full bg-white">
        {/* Video Container - Full Width on mobile */}
        <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] sm:w-full sm:left-0 sm:right-0 sm:ml-0 sm:mr-0">
          <video 
            className="w-full aspect-auto" 
            loop 
            autoPlay 
            muted
            playsInline
          >
            <source src={RedwoodAssembly} type="video/mp4" />
          </video>
        </div>
        
        {/* Text content - properly centered */}
        <div className="text-center max-w-6xl mx-auto pt-6 pb-4">
          <h2 className="text-[5vw] sm:text-2xl md:text-4xl font-bold text-warm-800 mb-4 whitespace-nowrap">
            Better | Faster | Stronger
          </h2>
          <p className="text-gray-600 text-base md:text-lg mx-auto mb-8 text-justify px-1 md:px-0">
            Homes designed with the Construx Framing System™ are constructed with interlocking, precision-cut panel components. These components are precision manufactured delivering a predictable, consistent, and repeatable result with accuracy and quality that outperforms traditional construction. Each component locks together with rigid connections and self aligning parts resulting in a flush, straight, plumb and square frame every time.
          </p>
          <Link 
            to="/product" 
            onClick={() => {
              window.scrollTo(0, 0);
              if (isExperiment) trackCTAClick('product');
            }}
            className="inline-block px-8 py-4 bg-construx-red text-white font-semibold rounded-lg hover:bg-construx-red-dark transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
          >
            Learn More
          </Link>
        </div>
      </section>
    </>
  );
}

// Hero Section Component
function HeroSection({ backgroundImage }) {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
      
      {/* Content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto">
        <h1 className="md:text-7xl sm:text-6xl text-2xl font-semibold text-white leading-tight mb-6">
          THE CONSTRUX
          <span className="block text-construx-red-light">FRAMING SYSTEM</span>
        </h1>
        <p className="md:text-3xl sm:text-4xl text-lg text-white/90 max-w-3xl mx-auto mb-10 font-bold leading-relaxed">
          A precision-manufactured building system that delivers quality homes faster, 
          with less waste and more control over your project.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/interest" 
            onClick={() => window.scrollTo(0, 0)}
            className="bg-[#a43424] w-[200px] rounded font-medium mx-auto py-3 text-white text-sm sm:text-base"
          >
            Start Your Project
          </Link>
          <Link 
            to="/models" 
            onClick={() => window.scrollTo(0, 0)}
            className="bg-white w-[200px] text-[#a43424] rounded font-medium mx-auto py-3 text-sm sm:text-base border-2 border-[#a43424] hover:bg-[#a43424] hover:text-white transition-all"
          >
            View Our Models
          </Link>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
}

// Benefits Section Component
function BenefitsSection() {
  return (
    <section className="pt-4 pb-20 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-800 mb-4">
            A Better Way to Build Homes
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            From design to assembly, the Construx Framing system helps you reduce cycle time, 
            optimize labor, and deliver exceptional quality.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12">
          {/* MORE Column */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-bold">+</span>
              </div>
              <h3 className="text-2xl font-bold text-green-700">MORE</h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Speed:</strong> Homes assembled in days, not months
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Performance:</strong> Factory precision leads to tighter, more energy-efficient homes
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Control:</strong> Remove control and replace with
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Efficiency:</strong> Construx's engineered designs save time with labor, permitting and inspections
                </span>
              </li>
            </ul>
          </div>
          
          {/* LESS Column */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border border-red-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-construx-red rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-bold">−</span>
              </div>
              <h3 className="text-2xl font-bold text-construx-red">LESS</h3>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-construx-red mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Risk:</strong> Predictable schedules and budgets with rigorous quality control
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-construx-red mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Waste:</strong> Precisely measured and machined materials reduce cutting and drilling on site
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-construx-red mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Labor Challenges:</strong> Reduce dependency on skilled framing crews
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FaCheckCircle className="text-construx-red mt-1 flex-shrink-0" />
                <span className="text-gray-700">
                  <strong>Complexity:</strong> Fewer subcontractors means simpler project management
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// Process Steps Component
function ProcessSection() {
  const steps = [
    { 
      icon: FaRulerCombined, 
      title: 'Design', 
      description: 'Work with us to customize your home or choose from our model library'
    },
    { 
      icon: FaCogs, 
      title: 'Manufacture', 
      description: 'Components are precision-cut in our factory'
    },
    { 
      icon: FaTruck, 
      title: 'Deliver', 
      description: 'Flat-packed components ship efficiently to your job site'
    },
    { 
      icon: FaTools, 
      title: 'Assemble', 
      description: 'Frame goes up quickly with our intuitive assembly system'
    },
    { 
      icon: FaHome, 
      title: 'Complete', 
      description: 'Finish with your preferred trades and move in faster'
    },
  ];

  return (
    <section className="py-20 bg-warm-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="w-16 h-1 bg-construx-red mx-auto mb-6 rounded-full" />
          <h2 className="text-3xl md:text-4xl font-bold text-warm-800 mb-4">
            How It Works
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            From design to move-in, we support you every step of the way
          </p>
        </div>
        
        {/* Steps */}
        <div className="relative">
          {/* Connection line - desktop only */}
          <div className="hidden lg:block absolute top-16 left-[10%] right-[10%] h-0.5 bg-gray-200" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative flex flex-col items-center text-center group">
                {/* Icon circle */}
                <div className="relative z-10 w-32 h-32 bg-white rounded-full shadow-lg flex items-center justify-center mb-6 border-4 border-gray-100 group-hover:border-construx-red transition-colors duration-300">
                  <step.icon className="text-4xl text-construx-red" />
                  
                  {/* Step number - inside circle top right */}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-construx-red text-white rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                    {index + 1}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-warm-800 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Stats Section
function StatsSection() {
  const stats = [
    { value: '50%', label: 'Faster Build Time', icon: FaClock },
    { value: '30%', label: 'Less Material Waste', icon: FaLeaf },
    { value: '100%', label: 'Precision Manufactured', icon: FaShieldAlt },
  ];

  return (
    <section className="py-16 bg-warm-900 text-white">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <stat.icon className="text-4xl text-construx-red-light mx-auto mb-4" />
              <div className="text-5xl md:text-6xl font-bold mb-2">{stat.value}</div>
              <div className="text-gray-400 text-lg">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Feature Cards Section
function FeatureCardsSection() {
  const features = [
    {
      icon: FaCogs,
      title: 'Digital Manufacturing',
      description: 'Every component is precision-cut to exact specification, ensuring perfect fits and minimal waste.'
    },
    {
      icon: FaLeaf,
      title: 'Sustainable Building',
      description: 'Our process reduces material waste by up to 30% compared to traditional stick-built construction.'
    },
    {
      icon: FaShieldAlt,
      title: 'Quality Assured',
      description: 'Factory-controlled conditions mean consistent quality regardless of weather or job site variables.'
    },
    {
      icon: FaHandshake,
      title: 'Builder Friendly',
      description: 'Designed for professional builders, our system integrates seamlessly with standard construction practices.'
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="w-16 h-1 bg-construx-red mx-auto mb-6 rounded-full" />
          <h2 className="text-3xl md:text-4xl font-bold text-warm-800 mb-4">
            Built Different
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            The Construx Framing System represents a new approach to residential construction
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group p-8 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-xl hover:border-construx-red/20 transition-all duration-300"
            >
              <div className="w-14 h-14 bg-construx-red/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-construx-red group-hover:scale-110 transition-all duration-300">
                <feature.icon className="text-2xl text-construx-red group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-warm-800 mb-3">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Video Section Component
function VideoSection({ videoUrl, posterImage }) {
  const [isPlaying, setIsPlaying] = useState(false);
  
  return (
    <section className="py-2 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-2">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-800 mb-12">
            See It In Action
          </h2>
        </div>
        
        <div className="relative rounded-2xl overflow-hidden shadow-2xl group bg-black">
          {!isPlaying ? (
            <>
              {/* Video thumbnail - using legacy truss install video as poster */}
              <video 
                className="w-full aspect-video object-cover"
                muted
                playsInline
                preload="metadata"
              >
                <source src="https://dgiy9ku8pat3z.cloudfront.net/videos/truss_install.mov" type="video/mp4" />
              </video>
              <div className="absolute inset-0 flex items-center justify-center">
                <button 
                  onClick={() => setIsPlaying(true)}
                  className="w-20 h-20 bg-construx-red rounded-full flex items-center justify-center text-white shadow-lg transform transition-all duration-300 hover:scale-110 hover:bg-construx-red-dark group-hover:scale-105"
                >
                  <FaPlay className="text-2xl ml-1" />
                </button>
              </div>
            </>
          ) : (
            <div className="relative aspect-video bg-black">
              <iframe
                src={`${videoUrl}?autoplay=1`}
                className="w-full h-full"
                allow="autoplay; fullscreen"
                allowFullScreen
                title="Construx Video"
              />
              <button 
                onClick={() => setIsPlaying(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              >
                <FaTimes />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Image Gallery - Links to Model Pages
function GallerySection({ images, trackCTAClick, isExperiment }) {
  const galleryImages = images.length > 0 ? images : [
    { url: '/placeholder1.jpg', title: 'Completed Maven Model', description: 'A stunning finished home built with the Construx system', slug: 'maven' },
    { url: '/placeholder2.jpg', title: 'Frame Assembly', description: 'Precision-manufactured components going up on site', slug: 'assembly' },
    { url: '/placeholder3.jpg', title: 'Interior View', description: 'Open floor plan showcasing the structural system', slug: 'interior' },
    { url: '/placeholder4.jpg', title: 'Manufacturing', description: 'Precision cutting in our factory facility', slug: 'manufacturing' },
  ];
  
  return (
    <section className="py-12 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-warm-800 mb-4">
            Multiple Design Options
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            From ADUs to multi-story homes, see what's possible with the Construx system
          </p>
        </div>
        
        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {galleryImages.map((image, index) => (
            <Link
              key={index}
              to={`/models/${image.slug}`}
              onClick={() => {
                window.scrollTo(0, 0);
                if (isExperiment) {
                  trackCTAClick('models', { modelName: image.title });
                }
              }}
              className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 p-4"
            >
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img 
                  src={image.url} 
                  alt={image.title}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-semibold text-sm">{image.title}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// Hotspot component with Floating UI
function Hotspot({ hotspot, isActive, onToggle }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const { refs, floatingStyles, context } = useFloating({
    open: isActive,
    onOpenChange: onToggle,
    placement: 'top',
    middleware: [
      offset(10),
      flip(),
      shift({ padding: 16 })
    ],
    whileElementsMounted: autoUpdate
  });
  
  const hover = useHover(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);
  
  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => onToggle(!isActive)}
        className={`relative w-[2rem] h-[2rem] md:w-[2.5rem] md:h-[2.5rem] ${hotspot.color} rounded-full flex items-center justify-center text-white shadow-lg transform transition-all duration-300 hover:scale-125 ${
          isActive ? 'scale-125 ring-4 ring-white/50' : ''
        }`}
      >
        <span className="font-bold text-xs md:text-sm">{hotspot.id}</span>
        {/* Pulse animation */}
        <span className={`absolute inset-0 ${hotspot.color} rounded-full animate-ping opacity-40`} />
      </button>
      
      {/* Floating Tooltip */}
      {isActive && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="w-64 sm:w-72 md:w-80 bg-white text-warm-800 rounded-xl shadow-2xl p-4 sm:p-5 z-50 max-w-[calc(100vw-2rem)]"
          >
            <h4 className="font-bold text-base sm:text-lg mb-2">{hotspot.title}</h4>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{hotspot.description}</p>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

// Interactive Hotspot Image Component
function HotspotSection({ backgroundImage }) {
  const [activeHotspot, setActiveHotspot] = useState(null);
  
  const hotspots = [
    {
      id: 1,
      x: 37,
      y: 68,
      title: 'Precision Manufacturing',
      description: 'Precision manufacturing delivers a flush, straight, plumb and square structure',
      color: 'bg-orange-500'
    },
    {
      id: 2,
      x: 70,
      y: 45,
      title: 'Rigid Tolerances',
      description: '1/16" tolerances with rigid self-aligning connections creates a superior structure',
      color: 'bg-blue-600'
    },
    {
      id: 3,
      x: 33,
      y: 92,
      title: 'Fully Integrated System',
      description: 'Fully integrated system includes roof, exterior walls, floors (and stairs)',
      color: 'bg-emerald-600'
    },
    {
      id: 4,
      x: 92,
      y: 15,
      title: 'Time and Cost Savings',
      description: 'Plumbers, electricians, drywallers can save time and money',
      color: 'bg-purple-600'
    },
  ];
  
  return (
    <section className="py-4 bg-white">
      <div className="max-w-6xl mx-auto">        
        <div className="relative rounded-2xl overflow-visible">
          <img 
            src={backgroundImage} 
            alt="Construx System"
            className="w-full aspect-video object-cover rounded-2xl"
          />
          
          {/* Hotspots */}
          {hotspots.map((hotspot) => (
            <div
              key={hotspot.id}
              className="absolute"
              style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
            >
              <Hotspot
                hotspot={hotspot}
                isActive={activeHotspot === hotspot.id}
                onToggle={(open) => setActiveHotspot(open ? hotspot.id : null)}
              />
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-4">
          {hotspots.map((hotspot) => (
            <button
              key={hotspot.id}
              onClick={() => setActiveHotspot(activeHotspot === hotspot.id ? null : hotspot.id)}
              className={`flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full transition-all duration-300 text-xs md:text-sm ${
                activeHotspot === hotspot.id 
                  ? 'bg-construx-red/20 text-warm-800 border border-construx-red' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className={`w-[0.75rem] h-[0.75rem] md:w-[1rem] md:h-[1rem] ${hotspot.color} rounded-full`} />
              <span className="text-xs md:text-sm font-medium">{hotspot.title}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// CTA Section
function CTASection({ trackCTAClick, isExperiment }) {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-warm-800">
          Ready to Learn More?
        </h2>
        <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
          Whether you're a builder looking for a better framing solution or interested in investing in the future of construction, we'd love to hear from you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/interest" 
            onClick={() => {
              window.scrollTo(0, 0);
              if (isExperiment) trackCTAClick('interest');
            }}
            className="px-8 py-4 bg-construx-red text-white font-semibold rounded-lg hover:bg-construx-red-dark transition-all duration-300 hover:shadow-lg"
          >
            Contact Us
          </Link>
          <Link 
            to="/faqs" 
            onClick={() => {
              window.scrollTo(0, 0);
              if (isExperiment) trackCTAClick('faqs');
            }}
            className="px-8 py-4 bg-white text-construx-red font-semibold rounded-lg border-2 border-construx-red hover:bg-construx-red hover:text-white transition-all duration-300"
          >
            Read FAQ
          </Link>
        </div>
      </div>
    </section>
  );
}

// Main Page Component
export default function System({ isExperiment = false }) {
  const content = useContentful();
  
  // Always call hooks (React rules), but tracking only happens if isExperiment is true
  useScrollDepthTracking('homepage-test', 'system', isExperiment);
  const { trackCTAClick } = useCTATracking('homepage-test', 'system', isExperiment);
  
  // Get images from content
  const heroImage = getImageUrlByName(content.images, 'shop-demo') || 
                    getImageUrlByName(content.images, 'maven-inside-view');
  const mavenInsideView = getImageUrlByName(content.images, 'maven-inside-view');
  // frameImage available if needed: getImageUrlByName(content.images, 'construx-frame')
  
  // Build gallery images from available content
  const galleryImages = content.models?.slice(0, 4).map(model => ({
    url: model.image?.fields?.file?.url || heroImage,
    title: model.name,
    description: `The ${model.name} - ${model.features?.[0] || 'A Construx Model'}`,
    slug: model.slug || model.name?.toLowerCase().replace(/\s+/g, '-')
  })) || [];

  return (
    <div className="font-bai px-10 md:px-20">
      <Helmet>
        <title>Home</title>
      </Helmet>
      
      <AssemblyVideoSection trackCTAClick={trackCTAClick} isExperiment={isExperiment} />
      {/* <HeroSection backgroundImage={heroImage} /> */}
      <HotspotSection backgroundImage={ConstruxFrameImage} />
      <GallerySection images={galleryImages} trackCTAClick={trackCTAClick} isExperiment={isExperiment} />
      <VideoSection 
        videoUrl="https://www.youtube.com/embed/ZiUB_Chdseo" 
        posterImage={mavenInsideView || heroImage} 
      />
      {/* <StatsSection /> */}
      {/* <ProcessSection /> */}
      {/* <FeatureCardsSection /> */}
      <CTASection trackCTAClick={trackCTAClick} isExperiment={isExperiment} />
    </div>
  );
}

